import React, { useState } from "react";
import { colors } from "../../utils/designTokens";
import {
  TIPOS_ASAMBLEA,
  CONVOCATORIAS,
  TIPOS_RESOLUCION,
  METODOS_VOTACION,
  SENTIDOS,
  emptyPunto,
  esVotanteExterno,
  externoIdDesdeNombre,
  nombreVotante,
} from "../../utils/actaConfig";
import { personasPresentes, calcularVotacion, tablaAlicuotas, calcularQuorum } from "../../utils/actaVoting";
import { umbralQuorum as umbralPorConvocatoria, etiquetaCampo } from "./actaFlujo";

const EXT_OPTION = "__ext__";

// Formulario de edición del acta. Vive en el panel derecho de la pantalla.
// Recibe el acta completa + el padrón (edificio, copropietarios) y un setter;
// cualquier cambio se propaga al preview en vivo.
//
// Props:
//   acta:             Acta
//   onChange:         (nextActa) => void
//   edificio:         { id, nombre, unidades: [...] }
//   copropietarios:   [{ id, nombre, ci, email }]
//   readOnly:         acta publicada (o con una minuta aplicándose): todo deshabilitado
//   avisoSoloLectura: texto que explica por qué está bloqueada
//   marcasPorPunto:   Map(puntoId → [{ campo, motivo }]) de la revisión de la IA
//   numeroAlPublicar: el N° de acta lo asigna el servidor al publicar (campo de solo lectura)

export default function ActaForm({
  acta,
  onChange,
  edificio,
  copropietarios = [],
  readOnly = false,
  avisoSoloLectura = "",
  marcasPorPunto = null,
  numeroAlPublicar = false,
}) {
  const a = acta;
  const coproById = new Map(copropietarios.map((c) => [String(c.id), c]));
  const unidadById = new Map((edificio?.unidades || []).map((u) => [String(u.id), u]));

  // Alícuotas efectivas (stored o reparto equitativo) + quórum en vivo.
  const alic = tablaAlicuotas(edificio);
  const hayAlicuotas = (edificio?.unidades || []).some((u) => typeof u.alicuota === "number");
  const umbralQuorum = umbralPorConvocatoria(a.convocatoria);
  const quorum = calcularQuorum(a, edificio, umbralQuorum);
  const fmtPct = (n) => `${Number(n || 0).toLocaleString("es-UY", { maximumFractionDigits: 2 })}%`;

  // En solo lectura ningún handler propaga (además del fieldset deshabilitado).
  const emitir = (next) => {
    if (!readOnly) onChange(next);
  };
  const setField = (key, value) => emitir({ ...a, [key]: value });

  // ── Asistencia ──
  const setAsistencia = (unidadId, patch) =>
    emitir({
      ...a,
      asistencia: a.asistencia.map((row) =>
        String(row.unidadId) === String(unidadId) ? { ...row, ...patch } : row
      ),
    });
  const setTodosPresentes = (presente) =>
    emitir({
      ...a,
      asistencia: a.asistencia.map((row) => ({
        ...row,
        presente,
        votanteId: row.votanteId || unidadById.get(String(row.unidadId))?.titularId || "",
      })),
    });

  // Cambia el votante de una unidad: copropietario del padrón o apoderado externo.
  const setVotante = (unidadId, value) => {
    if (value === EXT_OPTION) {
      setAsistencia(unidadId, { votanteId: externoIdDesdeNombre("", `u${unidadId}`), votanteNombre: "" });
    } else {
      setAsistencia(unidadId, { votanteId: value, votanteNombre: "" });
    }
  };
  const setVotanteExtNombre = (unidadId, nombre) => {
    setAsistencia(unidadId, {
      votanteId: externoIdDesdeNombre(nombre, `u${unidadId}`),
      votanteNombre: nombre,
    });
  };

  // ── Orden del día ──
  const renumber = (puntos) => puntos.map((p, i) => ({ ...p, numero: i + 1 }));
  const setPuntos = (puntos) => emitir({ ...a, ordenDelDia: renumber(puntos) });
  const setPunto = (id, patch) =>
    setPuntos(a.ordenDelDia.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const addPunto = () => setPuntos([...a.ordenDelDia, emptyPunto(a.ordenDelDia.length + 1)]);
  const removePunto = (id) => setPuntos(a.ordenDelDia.filter((p) => p.id !== id));
  const movePunto = (id, dir) => {
    const idx = a.ordenDelDia.findIndex((p) => p.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= a.ordenDelDia.length) return;
    const next = [...a.ordenDelDia];
    [next[idx], next[j]] = [next[j], next[idx]];
    setPuntos(next);
  };

  // Voto de una persona en un punto: setea el sentido (o lo quita si sentido === "").
  const setVoto = (puntoId, personId, sentido) => {
    const punto = a.ordenDelDia.find((p) => p.id === puntoId);
    if (!punto) return;
    const vpp = { favor: [], contra: [], abstencion: [], ...(punto.votacion?.votosPorPersona || {}) };
    const next = {};
    for (const s of ["favor", "contra", "abstencion"]) {
      next[s] = (vpp[s] || []).filter((id) => String(id) !== String(personId));
    }
    if (sentido) next[sentido] = [...next[sentido], String(personId)];
    setPunto(puntoId, { votacion: { ...punto.votacion, votosPorPersona: next } });
  };

  // Votantes presentes (copropietarios + apoderados externos) con nombre legible.
  const presentes = personasPresentes(a).map((id) => ({
    id,
    nombre: nombreVotante(id, a.asistencia, coproById),
  }));

  return (
    <aside style={S.panel} aria-label="Editar acta">
      <header style={S.header}>
        <h2 style={S.headerTitle}>{readOnly ? "Acta" : "Editar acta"}</h2>
        <p style={S.headerSub}>
          {readOnly
            ? avisoSoloLectura || "Solo lectura."
            : "Los cambios se reflejan al instante en la vista previa."}
        </p>
      </header>

      {/* ───────── DATOS GENERALES ───────── */}
      <Accordion title="Datos de la asamblea" defaultOpen disabled={readOnly}>
        <Row>
          <Select label="Tipo" value={a.tipo} options={TIPOS_ASAMBLEA} onChange={(v) => setField("tipo", v)} />
          <Select label="Convocatoria" value={a.convocatoria} options={CONVOCATORIAS} onChange={(v) => setField("convocatoria", v)} />
        </Row>
        <Row>
          <Text label="Fecha" type="date" value={a.fecha} onChange={(v) => setField("fecha", v)} />
          <Text label="Ciudad" value={a.ciudad} onChange={(v) => setField("ciudad", v)} />
        </Row>
        <Row>
          <Text label="Hora apertura" type="time" value={a.horaApertura} onChange={(v) => setField("horaApertura", v)} />
          <Text label="Hora cierre" type="time" value={a.horaCierre} onChange={(v) => setField("horaCierre", v)} />
        </Row>
        <Row>
          {numeroAlPublicar ? (
            <Text
              label="N° de acta"
              value={a.actaNumero}
              onChange={() => {}}
              placeholder="Se asigna al publicar"
              readOnly
              title="El número correlativo del edificio se asigna al publicar el acta."
            />
          ) : (
            <Text label="N° de acta" value={a.actaNumero} onChange={(v) => setField("actaNumero", v)} placeholder="2026-01" />
          )}
          <Text label="Folio" value={a.folio} onChange={(v) => setField("folio", v)} />
          <Text label="Libro" value={a.libro} onChange={(v) => setField("libro", v)} />
        </Row>
        <Row>
          <Text label="Edificio" value={a.edificioNombre} onChange={(v) => setField("edificioNombre", v)} />
          <Text label="Padrón" value={a.padron} onChange={(v) => setField("padron", v)} />
        </Row>
      </Accordion>

      {/* ───────── TEXTOS EDITORIALES ───────── */}
      <Accordion title="Textos del documento" disabled={readOnly}>
        <Text label="Epígrafe (portada)" value={a.epigrafe} onChange={(v) => setField("epigrafe", v)} />
        <TextArea label="Lead de portada" value={a.leadPortada} onChange={(v) => setField("leadPortada", v)} rows={3} />
        <TextArea
          label="Apertura"
          value={a.textoApertura}
          onChange={(v) => setField("textoApertura", v)}
          rows={4}
          placeholder="Vacío = se genera automáticamente con ciudad, fecha y convocatoria."
        />
        <TextArea label="Cierre" value={a.textoCierre} onChange={(v) => setField("textoCierre", v)} rows={3} />
      </Accordion>

      {/* ───────── ASISTENCIA ───────── */}
      <Accordion title={`Asistencia · ${a.asistencia.filter((r) => r.presente).length}/${a.asistencia.length} presentes`} defaultOpen disabled={readOnly}>
        {a.asistencia.length === 0 ? (
          <p style={S.helper}>Este edificio no tiene unidades cargadas. Cargalas en “Editar edificio”.</p>
        ) : (
          <>
            <div style={S.quorumBox}>
              <div style={S.quorumRow}>
                <span style={S.quorumLabel}>Quórum presente</span>
                <span style={S.quorumValue}>{fmtPct(quorum.coeficientePresente)}</span>
              </div>
              <div style={S.quorumMeta}>
                {quorum.unidadesPresentes}/{quorum.unidadesTotales} unidades ·{" "}
                <span style={{ color: quorum.habilitante ? "#0A8F4F" : "#C24545" }}>
                  {quorum.habilitante ? "Habilitante" : "No habilitante"}
                </span>{" "}
                (umbral {umbralQuorum}% · {a.convocatoria === "primera" ? "1ª" : "2ª"} conv.)
              </div>
              <div style={S.quorumMeta}>
                {hayAlicuotas
                  ? "Mayoría = >50% del coeficiente computado en cada votación."
                  : "Sin alícuotas cargadas: se reparte 100% en partes iguales. Mayoría = >50%."}
              </div>
            </div>
            <div style={S.toggleAllRow}>
              <button type="button" onClick={() => setTodosPresentes(true)} style={S.toggleAllBtn}>Marcar todos</button>
              <button type="button" onClick={() => setTodosPresentes(false)} style={S.toggleAllBtn}>Limpiar</button>
            </div>
            <p style={S.helper}>El votante por defecto es el titular. Cambialo para registrar un poder (apoderado que vota por la unidad).</p>
            {(edificio?.unidades || []).map((u) => {
              const row = a.asistencia.find((r) => String(r.unidadId) === String(u.id)) || {};
              const titular = coproById.get(String(u.titularId));
              const isExt = esVotanteExterno(row.votanteId);
              return (
                <div key={u.id} style={S.asistRow}>
                  <label style={S.asistCheck}>
                    <input
                      type="checkbox"
                      checked={!!row.presente}
                      onChange={(e) => setAsistencia(u.id, { presente: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: colors.primary, cursor: "pointer" }}
                    />
                    <span style={S.asistUnidadCol}>
                      <span style={S.asistUnidad}>{u.etiqueta}</span>
                      <span style={S.asistPct}>{fmtPct(alic[u.id])}</span>
                    </span>
                  </label>
                  <div style={{ flex: 1 }}>
                    <div style={S.asistTitular}>{titular?.nombre || "Sin titular"}</div>
                    {row.presente && (
                      <>
                        <select
                          value={isExt ? EXT_OPTION : (row.votanteId || u.titularId || "")}
                          onChange={(e) => setVotante(u.id, e.target.value)}
                          style={S.miniSelect}
                          aria-label={`Votante de unidad ${u.etiqueta}`}
                        >
                          {copropietarios.map((c) => (
                            <option key={c.id} value={c.id}>
                              Vota: {c.nombre}{String(c.id) === String(u.titularId) ? " (titular)" : ""}
                            </option>
                          ))}
                          <option value={EXT_OPTION}>Otro (apoderado externo)…</option>
                        </select>
                        {isExt && (
                          <input
                            type="text"
                            value={row.votanteNombre || ""}
                            onChange={(e) => setVotanteExtNombre(u.id, e.target.value)}
                            placeholder="Nombre del apoderado externo"
                            style={{ ...S.miniSelect, marginTop: 4 }}
                            aria-label={`Nombre del apoderado externo de la unidad ${u.etiqueta}`}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </Accordion>

      {/* ───────── ORDEN DEL DÍA ───────── */}
      <Accordion title={`Orden del día · ${a.ordenDelDia.length} puntos`} defaultOpen disabled={readOnly}>
        <p style={S.helper}>Cada punto es una resolución. El “Título de sección” (opcional) agrupa puntos bajo un encabezado en el PDF.</p>
        {a.ordenDelDia.map((p, idx) => (
          <PuntoEditor
            key={p.id}
            punto={p}
            index={idx}
            total={a.ordenDelDia.length}
            presentes={presentes}
            acta={a}
            edificio={edificio}
            marcas={(marcasPorPunto && marcasPorPunto.get(String(p.id))) || []}
            onPatch={(patch) => setPunto(p.id, patch)}
            onVotacionPatch={(patch) => setPunto(p.id, { votacion: { ...p.votacion, ...patch } })}
            onSetVoto={(personId, sentido) => setVoto(p.id, personId, sentido)}
            onMove={(dir) => movePunto(p.id, dir)}
            onRemove={() => removePunto(p.id)}
          />
        ))}
        {!readOnly && <button type="button" onClick={addPunto} style={S.addBtn}>+ Agregar punto</button>}
      </Accordion>

      {/* ───────── FIRMAS ───────── */}
      <Accordion title="Firmas" disabled={readOnly}>
        <FirmaPicker
          rol="Presidencia"
          idValue={a.presidenciaId}
          subValue={a.presidenciaSub}
          copropietarios={copropietarios}
          onId={(v) => setField("presidenciaId", v)}
          onSub={(v) => setField("presidenciaSub", v)}
        />
        <FirmaPicker
          rol="Secretaría"
          idValue={a.secretariaId}
          subValue={a.secretariaSub}
          copropietarios={copropietarios}
          onId={(v) => setField("secretariaId", v)}
          onSub={(v) => setField("secretariaSub", v)}
        />
        <Text label="Administración" value={a.administracion} onChange={(v) => setField("administracion", v)} />
        <Text label="Detalle administración" value={a.administracionDetalle} onChange={(v) => setField("administracionDetalle", v)} placeholder="Reg. MEC N° 000" />
      </Accordion>
    </aside>
  );
}

// ───────────────────────── Editor de un punto ─────────────────────────
function PuntoEditor({ punto, index, total, presentes, acta, edificio, marcas = [], onPatch, onVotacionPatch, onSetVoto, onMove, onRemove }) {
  const p = punto;
  const esInformativa = p.tipoResolucion === "informativa";
  const votos = p.votacion?.votosPorPersona || { favor: [], contra: [], abstencion: [] };
  const sentidoDe = (personId) => {
    for (const s of ["favor", "contra", "abstencion"]) {
      if ((votos[s] || []).some((id) => String(id) === String(personId))) return s;
    }
    return "";
  };
  const res = esInformativa ? null : calcularVotacion(p.votacion, acta, edificio, p.tipoResolucion);
  // ids únicos por punto: los labels apuntan a SU input (no al del primer punto).
  const idBase = `acta-pt-${String(p.id).replace(/[^A-Za-z0-9_-]/g, "")}`;

  return (
    <div style={{ ...S.puntoCard, ...(marcas.length ? S.puntoCardMarcado : {}) }} id={`acta-punto-${p.id}`}>
      <div style={S.puntoHead}>
        <span style={S.puntoNum}>Punto {String(index + 1).padStart(2, "0")}</span>
        <div style={{ display: "flex", gap: 4 }}>
          <IconBtn label="Subir" disabled={index === 0} onClick={() => onMove(-1)}>↑</IconBtn>
          <IconBtn label="Bajar" disabled={index === total - 1} onClick={() => onMove(1)}>↓</IconBtn>
          <IconBtn label="Eliminar" danger onClick={onRemove}>✕</IconBtn>
        </div>
      </div>
      {marcas.length > 0 && (
        <div style={S.marcaBox} role="note" aria-label={`Marcas de revisión del punto ${index + 1}`}>
          <span style={S.marcaTitulo}>Revisar</span>
          {marcas.map((m, i) => (
            <span key={i} style={S.marcaTexto}>
              {m.campo ? `${etiquetaCampo(m.campo)}: ` : ""}
              {m.motivo || "la IA no está segura de este dato."}
            </span>
          ))}
        </div>
      )}
      <Text id={`${idBase}-seccion`} label="Título de sección (opcional)" value={p.seccion} onChange={(v) => onPatch({ seccion: v })} placeholder="Ej: Cuentas y presupuesto" />
      <Text id={`${idBase}-titulo`} label="Título del punto" value={p.titulo} onChange={(v) => onPatch({ titulo: v })} />
      <TextArea
        id={`${idBase}-cuerpo`}
        label="Cuerpo / desarrollo"
        value={p.cuerpo}
        onChange={(v) => onPatch({ cuerpo: v })}
        rows={3}
        hint={AYUDA_FORMATO}
      />
      <Select id={`${idBase}-tipo`} label="Tipo de resolución" value={p.tipoResolucion} options={TIPOS_RESOLUCION} onChange={(v) => onPatch({ tipoResolucion: v })} />
      <TextArea
        id={`${idBase}-resolucion`}
        label={esInformativa ? "Texto informativo" : "Texto de la resolución"}
        value={p.textoResolucion}
        onChange={(v) => onPatch({ textoResolucion: v })}
        rows={2}
        hint={AYUDA_FORMATO}
      />

      {!esInformativa && (
        <div style={S.votacionBox}>
          <div style={S.votacionHeadRow}>
            <span style={S.miniLabel}>Votación</span>
            <div style={S.metodoToggle}>
              {METODOS_VOTACION.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => onVotacionPatch({ metodo: m.value })}
                  style={{
                    ...S.metodoBtn,
                    ...(p.votacion?.metodo === m.value ? S.metodoBtnActive : {}),
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {presentes.length === 0 ? (
            <p style={S.helper}>Marcá unidades presentes en “Asistencia” para poder registrar votos.</p>
          ) : (
            presentes.map((c) => (
              <div key={c.id} style={S.votoRow}>
                <span style={S.votoName}>{c.nombre}</span>
                <select
                  value={sentidoDe(c.id)}
                  onChange={(e) => onSetVoto(c.id, e.target.value)}
                  style={S.miniSelect}
                  aria-label={`Voto de ${c.nombre}`}
                >
                  <option value="">— Sin voto</option>
                  {SENTIDOS.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            ))
          )}

          {res && (
            <div style={S.resLine}>
              <span style={{ color: SENTIDOS[0].color }}>● {res.favor}</span>
              <span style={{ color: SENTIDOS[1].color }}>● {res.contra}</span>
              <span style={{ color: SENTIDOS[2].color }}>● {res.abstencion}</span>
              <span style={{ marginLeft: "auto", color: res.aprobado ? "#0A8F4F" : "#C24545" }}>
                {res.aprobado ? "Aprobada" : "No aprobada"}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FirmaPicker({ rol, idValue, subValue, copropietarios, onId, onSub }) {
  const id = `acta-firma-${rol.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()}`;
  return (
    <div style={{ marginBottom: 8 }}>
      <label htmlFor={id} style={S.label}>{rol}</label>
      <select id={id} value={idValue || ""} onChange={(e) => onId(e.target.value)} style={{ ...S.input, marginBottom: 6 }}>
        <option value="">— Sin asignar</option>
        {copropietarios.map((c) => (
          <option key={c.id} value={c.id}>{c.nombre}</option>
        ))}
      </select>
      <input
        type="text"
        value={subValue || ""}
        onChange={(e) => onSub(e.target.value)}
        placeholder="Unidad · C.I. (opcional)"
        style={S.input}
        aria-label={`Detalle de ${rol}`}
      />
    </div>
  );
}

// ───────────────────────── Subcomponentes UI ─────────────────────────
// `disabled` deshabilita todo el contenido con un <fieldset>: el botón del
// acordeón queda afuera para poder abrir y leer las secciones.
function Accordion({ title, children, defaultOpen = false, disabled = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section style={S.section}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={S.accordionBtn} aria-expanded={open}>
        <span style={S.sectionTitle}>{title}</span>
        <span style={S.accordionChevron}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <fieldset disabled={disabled} style={S.sectionBody}>
          {children}
        </fieldset>
      )}
    </section>
  );
}

function Row({ children }) {
  return <div style={{ display: "flex", gap: 8 }}>{children}</div>;
}

const idDe = (prefijo, label) => `${prefijo}-${label.replace(/\s+/g, "-").toLowerCase()}`;

function Text({ id: idProp, label, value, onChange, type = "text", placeholder, readOnly = false, title }) {
  const id = idProp || idDe("acta", label);
  return (
    <div style={S.field}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <input
        id={id}
        type={type}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        title={title}
        style={{ ...S.input, ...(readOnly ? S.inputReadOnly : {}) }}
      />
    </div>
  );
}

/** Cómo se escriben los saltos de renglón y las viñetas (ActaTemplate los respeta). */
const AYUDA_FORMATO = "Cada renglón sale como renglón en el acta. Para una viñeta, empezá el renglón con un guion (-).";

function TextArea({ id: idProp, label, value, onChange, rows = 3, placeholder, hint }) {
  const id = idProp || idDe("acta-ta", label);
  const idHint = hint ? `${id}-ayuda` : undefined;
  return (
    <div style={S.field}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <textarea
        id={id}
        rows={rows}
        value={value || ""}
        placeholder={placeholder}
        aria-describedby={idHint}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...S.input, resize: "vertical", lineHeight: 1.5 }}
      />
      {hint && <span id={idHint} style={S.ayuda}>{hint}</span>}
    </div>
  );
}

function Select({ id: idProp, label, value, options, onChange }) {
  const id = idProp || idDe("acta-sel", label);
  return (
    <div style={S.field}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} style={S.input}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function IconBtn({ children, onClick, label, disabled, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      style={{
        ...S.iconBtn,
        ...(disabled ? S.iconBtnDisabled : {}),
        ...(danger ? S.iconBtnDanger : {}),
      }}
    >
      {children}
    </button>
  );
}

// ───────────────────────── Estilos ─────────────────────────
const S = {
  // El ancho, el borde y el scroll los pone la columna del Editor (que también
  // aloja el panel de revisión).
  panel: {
    background: colors.white,
    padding: "20px 18px 32px",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 400,
  },
  header: { marginBottom: 18 },
  headerTitle: { margin: 0, fontSize: 16, fontWeight: 400, color: colors.black, letterSpacing: "-0.01em" },
  headerSub: { margin: "4px 0 0", fontSize: 12, color: colors.gray600, fontWeight: 400, lineHeight: 1.5 },
  section: { paddingBottom: 12, marginBottom: 12, borderBottom: `1px solid ${colors.gray100}` },
  accordionBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "transparent",
    border: 0,
    padding: "4px 0",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  accordionChevron: { fontSize: 12, color: colors.gray400 },
  sectionTitle: { fontSize: 11, fontWeight: 400, color: colors.gray600, letterSpacing: "0.06em", textTransform: "uppercase" },
  // fieldset sin el borde/padding del navegador, con el mismo layout que tenía el div.
  sectionBody: { display: "flex", flexDirection: "column", gap: 10, margin: "10px 0 0", padding: 0, border: 0, minWidth: 0 },
  helper: { margin: "0 0 4px", fontSize: 11, color: colors.gray600, lineHeight: 1.4 },
  field: { display: "flex", flexDirection: "column", gap: 5, flex: 1 },
  label: { fontSize: 11, color: colors.gray700, fontWeight: 400, display: "block", marginBottom: 4 },
  ayuda: { fontSize: 11, color: colors.gray400, lineHeight: 1.4 },
  input: {
    width: "100%",
    border: `1px solid ${colors.gray300}`,
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 13,
    color: colors.black,
    fontFamily: "inherit",
    fontWeight: 400,
    outline: "none",
    background: colors.white,
  },
  inputReadOnly: { background: colors.gray50, color: colors.gray600 },
  toggleAllRow: { display: "flex", gap: 8 },
  toggleAllBtn: {
    flex: 1,
    background: colors.gray100,
    color: colors.gray700,
    border: `1px solid ${colors.gray200}`,
    borderRadius: 6,
    padding: "6px 8px",
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  asistRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "8px 0",
    borderBottom: `1px solid ${colors.gray100}`,
  },
  asistCheck: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer", minWidth: 72, paddingTop: 2 },
  asistUnidadCol: { display: "flex", flexDirection: "column", lineHeight: 1.1 },
  asistUnidad: { fontSize: 13, color: colors.primary, fontVariantNumeric: "tabular-nums" },
  asistPct: { fontSize: 10, color: colors.gray600, fontVariantNumeric: "tabular-nums" },
  quorumBox: {
    background: colors.primarySubtle,
    border: `1px solid ${colors.primaryLighter || colors.gray200}`,
    borderRadius: 8,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  quorumRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between" },
  quorumLabel: { fontSize: 11, color: colors.gray700, letterSpacing: "0.04em", textTransform: "uppercase" },
  quorumValue: { fontSize: 18, color: colors.primaryDark, fontVariantNumeric: "tabular-nums" },
  quorumMeta: { fontSize: 11, color: colors.gray600, lineHeight: 1.4 },
  asistTitular: { fontSize: 13, color: colors.black, marginBottom: 4 },
  miniSelect: {
    width: "100%",
    border: `1px solid ${colors.gray300}`,
    borderRadius: 6,
    padding: "5px 8px",
    fontSize: 12,
    color: colors.gray700,
    fontFamily: "inherit",
    background: colors.white,
    outline: "none",
  },
  addBtn: {
    width: "100%",
    background: colors.primarySubtle,
    color: colors.primaryDark,
    border: `1px dashed ${colors.primary}`,
    borderRadius: 8,
    padding: "10px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: 4,
  },
  puntoCard: {
    border: `1px solid ${colors.gray200}`,
    borderRadius: 10,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: colors.bgMain,
  },
  puntoCardMarcado: { border: `1px solid ${colors.warning}` },
  marcaBox: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: colors.warningLight,
    borderRadius: 8,
    padding: "8px 10px",
  },
  marcaTitulo: { fontSize: 11, color: colors.warningDark, letterSpacing: "0.06em", textTransform: "uppercase" },
  marcaTexto: { fontSize: 12, color: colors.gray900, lineHeight: 1.4 },
  puntoHead: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  puntoNum: { fontSize: 11, color: colors.primary, letterSpacing: "0.08em", textTransform: "uppercase" },
  iconBtn: {
    width: 26,
    height: 26,
    borderRadius: 6,
    border: `1px solid ${colors.gray300}`,
    background: colors.white,
    color: colors.gray700,
    cursor: "pointer",
    fontSize: 12,
    lineHeight: 1,
    fontFamily: "inherit",
  },
  iconBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  iconBtnDanger: { color: colors.error, borderColor: colors.errorLight },
  votacionBox: {
    border: `1px solid ${colors.gray200}`,
    borderRadius: 8,
    padding: 10,
    background: colors.white,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  votacionHeadRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  miniLabel: { fontSize: 11, color: colors.gray600, letterSpacing: "0.06em", textTransform: "uppercase" },
  metodoToggle: { display: "flex", gap: 4 },
  metodoBtn: {
    border: `1px solid ${colors.gray300}`,
    background: colors.white,
    color: colors.gray600,
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  metodoBtnActive: { background: colors.primary, color: colors.white, borderColor: colors.primary },
  votoRow: { display: "flex", alignItems: "center", gap: 8 },
  votoName: { flex: 1, fontSize: 12, color: colors.gray700 },
  resLine: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    fontSize: 12,
    fontVariantNumeric: "tabular-nums",
    paddingTop: 6,
    borderTop: `1px solid ${colors.gray100}`,
  },
};
